// Subscription seat usage for one company (see `features/subscription-seats.md`).
// Shows the counts a plan will be measured against: the athletes and the coaches
// on the club's roster, and their total. Everyone the club trains is an athlete
// of the club here — where a profile is owned changes nothing about the bill, so
// the figure is never split by it.
//
// Rendered on the company profile page and on the phone-sized admin shell, so it
// comes in two densities: a titled card for the desktop profile, and a compact
// chip row for mobile, where vertical space is the scarce thing. Presentation
// only — the figures are aggregated server-side and the total comes from the
// domain, never from adding up in the view.

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { CompanySeatUsage } from '../domain';
import { useTranslation } from '../state/I18nContext';

interface SeatUsageSummaryProps {
  usage: CompanySeatUsage;
  /** Compact chip row instead of the titled card (the mobile admin shell). */
  dense?: boolean;
}

export function SeatUsageSummary({ usage, dense = false }: SeatUsageSummaryProps) {
  const { t } = useTranslation();

  // Label-above-number rather than "24 athletes": the figures then need no plural
  // agreement in any catalogue, and they line up as a scannable row.
  const parts = [
    { key: 'athletes', label: t('admin.seats.athletes'), value: usage.athletes },
    { key: 'coaches', label: t('admin.seats.coaches'), value: usage.coaches },
  ];

  if (dense) {
    return (
      <Paper variant="outlined" sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            {t('admin.seats.title')}
          </Typography>
          <Chip
            size="small"
            color="primary"
            label={t('admin.seats.seatCount', { count: usage.billableSeats })}
          />
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {parts.map((part) => (
            <Chip
              key={part.key}
              size="small"
              variant="outlined"
              label={`${part.label} ${part.value}`}
            />
          ))}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        {t('admin.seats.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('admin.seats.caption')}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
        }}
      >
        {parts.map((part) => (
          <Stat key={part.key} label={part.label} value={part.value} />
        ))}
        <Stat
          label={t('admin.seats.total')}
          value={usage.billableSeats}
          hint={t('admin.seats.totalHelp')}
          emphasis
        />
      </Box>
    </Paper>
  );
}

interface StatProps {
  label: string;
  value: number;
  hint?: string;
  /** The plan-relevant figure, so it reads as the headline of the row. */
  emphasis?: boolean;
}

function Stat({ label, value, hint, emphasis = false }: StatProps) {
  const tile = (
    <Box>
      <Typography
        variant="h5"
        color={emphasis ? 'primary.main' : 'text.primary'}
        sx={{ lineHeight: 1.2 }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
  return hint ? <Tooltip title={hint}>{tile}</Tooltip> : tile;
}
